import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, getToken, BRANCH_KEY } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PoStatusBadge } from "@/components/purchases/PoStatusBadge";
import { ReceptionDialog } from "@/components/purchases/ReceptionDialog";
import { ReceptionsHistory } from "@/components/purchases/ReceptionsHistory";
import { ReturnDialog } from "@/components/returns/ReturnDialog";
import { PrStatusBadge } from "@/components/returns/PrStatusBadge";
import { SupplierDocumentsCard } from "@/components/supplier-documents/SupplierDocumentsCard";
import { money } from "@/lib/constants";
import {
  ArrowLeft, RefreshCw, FileDown, CheckCircle2, Send, Ban, X,
  Info, Truck, MapPin, ClipboardList, Pencil, PackageCheck, PackageX,
} from "lucide-react";

function Row({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</dt>
      <dd className={`text-sm text-slate-800 ${mono ? "mono" : ""}`}>{value || "—"}</dd>
    </div>
  );
}

const EDITABLE_STATUSES = new Set(["borrador", "por_aprobar"]);
const CANCELABLE_STATUSES = new Set([
  "borrador", "por_aprobar", "aprobada", "emitida",
]);
const RECEIVABLE_STATUSES = new Set(["emitida", "recibida_parcial"]);

export default function CompraDetalle() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [parts, setParts] = useState([]);
  const [receptionOpen, setReceptionOpen] = useState(false);
  const [receptionsRefresh, setReceptionsRefresh] = useState(0);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returns, setReturns] = useState([]);

  // Dialogs
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const canEdit = hasPermission("purchase_orders:edit");
  const canApprove = hasPermission("purchase_orders:approve");
  const canCancel = hasPermission("purchase_orders:cancel");
  const canView = hasPermission("purchase_orders:view");
  const canReceive = hasPermission("purchase_orders:receive");
  const canReturn = hasPermission("purchase_returns:create");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-orders/${poId}`);
      setPo(data);
      // Load returns linked to this PO (for section rendering + returnable button)
      try {
        const { data: rs } = await api.get(`/purchase-orders/${poId}/returns`);
        setReturns(rs);
      } catch (_) { /* not critical */ }
      // Load parts catalog once (used by reception dialog for linking)
      if (parts.length === 0) {
        try {
          const { data: p } = await api.get("/parts", { params: {} });
          setParts(p);
        } catch (_) { /* not critical */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  useEffect(() => { load(); }, [load]);

  function openPdf() {
    const token = getToken();
    fetch(`${API_BASE}/purchase-orders/${poId}/pdf`, {
      headers: { Authorization: `Bearer ${token}`, "X-Branch-Id": localStorage.getItem(BRANCH_KEY) || "" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(`No se pudo generar el PDF: ${e.message}`));
  }

  async function doTransition(action, body = null, successMsg) {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${poId}/${action}`, body || {});
      setPo(data);
      toast.success(successMsg);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !po) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const status = po.status;
  const editable = EDITABLE_STATUSES.has(status);
  const canGoSubmit = canEdit && status === "borrador";
  const canGoApprove = canApprove && (status === "borrador" || status === "por_aprobar");
  const canGoReject = canApprove && status === "por_aprobar";
  const canGoEmit = canApprove && status === "aprobada";
  const canGoCancel = canCancel && CANCELABLE_STATUSES.has(status);
  const canGoReceive = canReceive && RECEIVABLE_STATUSES.has(status);
  // Fase 6.4 — availability for return button
  const hasReceivedInv = (po.lines || []).some(
    (l) => l.line_type === "inventariable" && Number(l.qty_received || 0) > 0,
  );
  const canRegisterReturn =
    canReturn && hasReceivedInv && !["cancelada", "rechazada"].includes(status);

  return (
    <div className="space-y-6" data-testid="page-compra-detalle">
      <Button variant="ghost" size="sm" onClick={() => navigate("/compras")} data-testid="po-detail-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a órdenes de compra
      </Button>

      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="mono text-tenant">{po.folio}</span>
            <PoStatusBadge status={po.status} data-testid="po-detail-status" />
          </span>
        }
        description={
          <span>
            {po.supplier_name} · {po.branch_name} · Tipo <b className="uppercase">{po.po_type}</b>
          </span>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} data-testid="po-detail-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
            </Button>
            {canView && (
              <Button variant="outline" size="sm" onClick={openPdf} data-testid="po-detail-pdf">
                <FileDown className="h-4 w-4 mr-1" /> PDF
              </Button>
            )}
            {editable && canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/compras/${po.id}/editar`)}
                data-testid="po-detail-edit"
              >
                <Pencil className="h-4 w-4 mr-1" /> Editar
              </Button>
            )}
            {canGoSubmit && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => doTransition("submit", null, "OC enviada a aprobación")}
                data-testid="po-detail-submit"
              >
                <Send className="h-4 w-4 mr-1" /> Enviar a aprobación
              </Button>
            )}
            {canGoApprove && (
              <Button
                size="sm"
                className="btn-tenant"
                disabled={busy}
                onClick={() => doTransition("approve", null, "OC aprobada")}
                data-testid="po-detail-approve"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar
              </Button>
            )}
            {canGoReject && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                disabled={busy}
                onClick={() => setRejectOpen(true)}
                data-testid="po-detail-reject"
              >
                <Ban className="h-4 w-4 mr-1" /> Rechazar
              </Button>
            )}
            {canGoEmit && (
              <Button
                size="sm"
                className="btn-tenant"
                disabled={busy}
                onClick={() => doTransition("emit", null, "OC emitida")}
                data-testid="po-detail-emit"
              >
                <Send className="h-4 w-4 mr-1" /> Emitir
              </Button>
            )}
            {canGoReceive && (
              <Button
                size="sm"
                className="btn-tenant"
                disabled={busy}
                onClick={() => setReceptionOpen(true)}
                data-testid="po-detail-receive"
              >
                <PackageCheck className="h-4 w-4 mr-1" /> Registrar recepción
              </Button>
            )}
            {canRegisterReturn && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setReturnOpen(true)}
                data-testid="po-detail-return"
              >
                <PackageX className="h-4 w-4 mr-1" /> Registrar devolución
              </Button>
            )}
            {canGoCancel && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                disabled={busy}
                onClick={() => setCancelOpen(true)}
                data-testid="po-detail-cancel"
              >
                <X className="h-4 w-4 mr-1" /> Cancelar
              </Button>
            )}
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Truck className="h-4 w-4" /> Proveedor</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{po.supplier_name || "—"}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4" /> Sucursal</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{po.branch_name} <span className="text-xs text-muted-foreground mono">({po.branch_code})</span></div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Info className="h-4 w-4" /> Emisión</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{po.issue_date}</div>
            <div className="text-xs text-muted-foreground">Esperada: {po.expected_date || "—"}</div>
          </CardContent>
        </Card>
      </div>

      {/* Header data */}
      <Card className="border-border">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> Datos de la orden
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Row label="Tipo" value={po.po_type} />
            <Row label="Moneda" value={po.currency} />
            <Row label="Creada por" value={po.created_by_name} />
            <Row label="Aprobada por" value={po.approved_by_name} />
            <Row label="Emitida por" value={po.emitted_by_name} />
            <Row label="Rechazada por" value={po.rejected_by_name} />
            <Row label="Cancelada por" value={po.cancelled_by_name} />
            <Row label="Fecha aprobación" value={po.approved_at ? new Date(po.approved_at).toLocaleString("es-MX") : "—"} />
            <div className="col-span-2 md:col-span-4">
              <Row label="Motivo / Uso" value={po.purpose} />
            </div>
            <div className="col-span-2 md:col-span-4">
              <Row label="Notas / Observaciones" value={po.notes} />
            </div>
            {po.reject_reason && (
              <div className="col-span-2 md:col-span-4">
                <Row label="Motivo del rechazo" value={po.reject_reason} />
              </div>
            )}
            {po.cancel_reason && (
              <div className="col-span-2 md:col-span-4">
                <Row label="Motivo de cancelación" value={po.cancel_reason} />
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Lines */}
      <Card className="border-border" data-testid="po-lines-card">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base">Partidas</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Cant.</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead className="text-right">Costo unitario</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Recibido</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(po.lines || []).map((l, idx) => (
                <TableRow key={l.id} data-testid={`po-detail-line-${idx}`}>
                  <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                  <TableCell className="text-xs uppercase">{l.line_type}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{l.description}</div>
                    {l.notes && <div className="text-xs text-muted-foreground">{l.notes}</div>}
                  </TableCell>
                  <TableCell className="text-right mono text-sm">{Number(l.quantity).toFixed(2)}</TableCell>
                  <TableCell className="text-xs">{l.unit}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(l.unit_cost)}</TableCell>
                  <TableCell className="text-right text-xs">{Math.round((l.iva_rate || 0) * 100)}%</TableCell>
                  <TableCell className="text-right mono text-sm">{money(l.line_total)}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    <div className="flex items-center gap-2 justify-end">
                      <span className="mono">
                        {Number(l.qty_received || 0).toFixed(2)} / {Number(l.quantity).toFixed(2)}
                      </span>
                      {(() => {
                        const s = l.line_status || "pending";
                        const styles = {
                          pending: "bg-slate-100 text-slate-700",
                          partial: "bg-amber-100 text-amber-800",
                          received: "bg-emerald-100 text-emerald-800",
                        };
                        const label = s === "received" ? "completa" : s === "partial" ? "parcial" : "pend";
                        return (
                          <span
                            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles[s]}`}
                            data-testid={`po-detail-line-${idx}-status`}
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end p-4 border-t border-border">
            <div className="text-right text-sm space-y-1" data-testid="po-detail-totals">
              <div>Subtotal: <span className="mono ml-2">{money(po.subtotal)}</span></div>
              <div>IVA: <span className="mono ml-2">{money(po.iva_amount)}</span></div>
              <div className="text-base font-semibold">Total: <span className="mono ml-2">{money(po.total)}</span></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        La recepción parcial se registra desde el botón <b>Registrar recepción</b> (solo disponible en estatus <span className="mono">emitida</span> o <span className="mono">recibida_parcial</span>).
        Solo las líneas <b>inventariables</b> generan movimientos de kardex; las líneas de servicio solo actualizan avance.
      </p>

      {(canView && (RECEIVABLE_STATUSES.has(status) || status === "recibida")) && (
        <ReceptionsHistory poId={po.id} refreshKey={receptionsRefresh} />
      )}

      {returns.length > 0 && (
        <Card className="border-border" data-testid="po-returns-linked">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PackageX className="h-4 w-4" /> Devoluciones ligadas ({returns.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-2">
            {returns.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between border border-border rounded-md p-2 text-sm hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/devoluciones/${r.id}`)}
                data-testid={`po-return-${r.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="mono font-medium text-tenant">{r.folio}</span>
                  <span className="text-xs text-muted-foreground">{r.return_date}</span>
                  <PrStatusBadge status={r.status} />
                </div>
                <div className="text-xs mono text-right">
                  <div>{money(r.ap_adjustment_amount)}</div>
                  {r.status === "emitida" && (
                    <div className={r.ap_adjusted ? "text-emerald-700" : "text-amber-700"}>
                      CxP {r.ap_adjusted ? "ajustada" : "pendiente"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <SupplierDocumentsCard entityType="po" entityId={po.id} testIdPrefix="po-docs" />

      <ReceptionDialog
        open={receptionOpen}
        onOpenChange={setReceptionOpen}
        po={po}
        parts={parts}
        onSuccess={(updated) => {
          if (updated) setPo(updated);
          else load();
          setReceptionsRefresh((v) => v + 1);
        }}
      />

      <ReturnDialog
        open={returnOpen}
        onOpenChange={setReturnOpen}
        poId={po.id}
        onSuccess={() => { load(); }}
      />

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="po-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar orden de compra</DialogTitle>
            <DialogDescription>
              Indica el motivo. La OC quedará en estatus <b>cancelada</b> y no podrá reactivarse.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Motivo *</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ej. Proveedor cambió el precio · producto no disponible"
              data-testid="po-cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Cerrar</Button>
            <Button
              className="btn-tenant"
              disabled={busy || !cancelReason.trim()}
              onClick={async () => {
                await doTransition("cancel", { reason: cancelReason.trim() }, "OC cancelada");
                setCancelOpen(false);
                setCancelReason("");
              }}
              data-testid="po-cancel-submit"
            >
              Cancelar OC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="po-reject-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar orden de compra</DialogTitle>
            <DialogDescription>
              La OC quedará en estatus <b>rechazada</b>. El creador podrá crear una nueva.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Motivo del rechazo *</Label>
            <Textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="po-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cerrar</Button>
            <Button
              className="btn-tenant"
              disabled={busy || !rejectReason.trim()}
              onClick={async () => {
                await doTransition("reject", { reason: rejectReason.trim() }, "OC rechazada");
                setRejectOpen(false);
                setRejectReason("");
              }}
              data-testid="po-reject-submit"
            >
              Rechazar OC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
